import { Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UserHttpService } from '../../core/services/user.http.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-forget-password',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './forget-password.html',
  styleUrl: './forget-password.scss',
})
export class ForgetPassword {
  readonly forgetPasswordForm: FormGroup;
  readonly isSubmitting = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly successMessage = signal<string>('');

  constructor(
    private fb: FormBuilder,
    private httpService: UserHttpService,
    private destroyRef: DestroyRef,
    private toast: ToastService,
    private route: ActivatedRoute,
  ) {
    this.forgetPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    const email = String(this.route.snapshot.queryParamMap.get('email') ?? '').trim();
    if (email) {
      this.forgetPasswordForm.patchValue({ email });
    }

    if (this.route.snapshot.queryParamMap.get('migrated') === '1') {
      this.successMessage.set('Your account was migrated. Send a reset link to set your new password.');
    }
  }

  onSubmit(): void {
    this.forgetPasswordForm.markAllAsTouched();
    if (this.forgetPasswordForm.invalid || this.isSubmitting()) {
      return;
    }

    const email = String(this.forgetPasswordForm.get('email')?.value ?? '').trim();
    this.isSubmitting.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    this.httpService.forgetPassword(email).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        const message = String(res?.message ?? 'Password reset link sent. Check your email.');
        this.successMessage.set(message);
        this.toast.success(message);
      },
      error: (error) => {
        this.isSubmitting.set(false);
        const apiMessage = String(error?.error?.message ?? '').trim();
        const message = apiMessage || 'Failed to send password reset email.';
        this.errorMessage.set(message);
        this.toast.error(message);
      },
    });
  }
}
